// ADR 25.9.26h §3 — every platform's install steps, as data.
//
// Each `title` and each element of `steps` is an i18n key; `InstallSteps`
// renders any entry. A new platform or a renamed browser menu is an edit here
// (and in the locales), never a new component. The English values quoting a
// browser's menu are provisional until 25.9.26h §0's capture.
//
// Named `installStepsTable.ts`, not the ADR's `installSteps.ts`: on a
// case-insensitive filesystem (macOS) `./InstallSteps` resolves `.ts` before
// `.tsx`, so the two names would collide.
//
// ADR 6.10.26a §3 — Chromium desktop leads with the address-bar install icon
// (the same one-confirmation dialogue the prompt opens), the menu kept as a
// `fallback` beneath it. The address-bar, Edge and Firefox wordings are
// provisional until that ADR's AC14 walk.
//
// ADR 16.10.26b §3 — iOS has no row: it gets `IosInstallDialog`'s one-line
// guide, not a steps list.
//
// ADR 16.10.26f §5 — nor does Mac Safari: it gets the install guide's
// stepper. Its keys (`pwa_mac_install_step_file` / `_dock`) are now read by
// `guide/guides.ts`' `mac_safari` entry.

import type { Platform } from './installAffordance'

/** Every platform that has steps — all but `other`, `ios` (16.10.26b §3) and `mac_safari` (16.10.26f §5). */
export type StepsPlatform = Exclude<Platform, 'other' | 'ios' | 'mac_safari'>

interface InstallStepsEntry {
  title: string
  steps: readonly string[]
  /** 6.10.26a §3a — a lead line and a second list, rendered after `steps`. */
  fallback?: { lead: string; steps: readonly string[] }
}

const BROWSER_TITLE = 'pwa_browser_install_title'
const COMPUTER_TITLE = 'pwa_install_strip_title_computer'
const ADDRESS_BAR_STEP = 'pwa_chromium_install_step_address_bar'
const MENU_FALLBACK_LEAD = 'pwa_chromium_install_menu_fallback'

export const INSTALL_STEPS: Record<StepsPlatform, InstallStepsEntry> = {
  chrome_desktop: {
    title: COMPUTER_TITLE,
    steps: [ADDRESS_BAR_STEP],
    fallback: {
      lead: MENU_FALLBACK_LEAD,
      steps: [
        'pwa_chrome_install_step_menu',
        'pwa_chrome_desktop_install_step_save',
        'pwa_chrome_desktop_install_step_install',
      ],
    },
  },
  edge_desktop: {
    title: COMPUTER_TITLE,
    steps: [ADDRESS_BAR_STEP],
    fallback: {
      lead: MENU_FALLBACK_LEAD,
      steps: ['pwa_edge_install_step_menu', 'pwa_edge_install_step_apps', 'pwa_edge_install_step_install'],
    },
  },
  // Chrome on Android shows no address-bar install icon: the menu stays first.
  chrome_android: {
    title: BROWSER_TITLE,
    steps: [
      'pwa_chrome_install_step_menu',
      'pwa_chrome_android_install_step_add',
      'pwa_chrome_android_install_step_install',
    ],
  },
  firefox_android: {
    title: BROWSER_TITLE,
    steps: ['pwa_firefox_android_install_step_menu', 'pwa_firefox_android_install_step_add'],
  },
  firefox_windows: {
    title: BROWSER_TITLE,
    steps: ['pwa_firefox_windows_install_step_bar', 'pwa_firefox_windows_install_step_save'],
  },
  // 15.10.26c §2 — the browsers phone makers ship. Samsung's wording is
  // replaced by the §0 probe before deploy.
  samsung_android: {
    title: BROWSER_TITLE,
    steps: ['pwa_samsung_install_step_menu', 'pwa_samsung_install_step_add'],
    fallback: {
      lead: 'pwa_samsung_install_fallback_lead',
      steps: ['pwa_samsung_install_fallback_icon'],
    },
  },
  // PROVISIONAL (15.10.26c §0) — generic wording from public sources, so a
  // slightly different label is still findable; 15.10.26d reports the rest.
  xiaomi_android: {
    title: BROWSER_TITLE,
    steps: ['pwa_xiaomi_install_step_menu', 'pwa_xiaomi_install_step_add'],
  },
  // PROVISIONAL (15.10.26c §0)
  huawei_android: {
    title: BROWSER_TITLE,
    steps: ['pwa_huawei_install_step_menu', 'pwa_huawei_install_step_add', 'pwa_huawei_install_step_badge'],
  },
  // PROVISIONAL (15.10.26c §0)
  oppo_android: {
    title: BROWSER_TITLE,
    steps: ['pwa_oppo_install_step_menu', 'pwa_oppo_install_step_add'],
  },
  // PROVISIONAL (15.10.26c §0)
  vivo_android: {
    title: BROWSER_TITLE,
    steps: ['pwa_vivo_install_step_menu', 'pwa_vivo_install_step_add'],
  },
}
